import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, OperatorFunction } from 'rxjs';
import { RollbarHandler } from 'app/common/rollbar-handler';
import { ValueHolder } from 'app/common/value-holder';

type Panel = '' | 'red-snackbar';
type Type = 'ERROR' | 'INFO';

class Message {
  public time: Date = new Date();
  constructor(public text: string, public panelClass: Panel, public type: Type) {}
}

export const LATEST_MESSAGES: ValueHolder<Message[]> = { value: [] };

/** Simple class to present a message via SnackBar */
@Injectable()
export class MessageHandlerService {
  constructor(private snackBar: MatSnackBar, private translate: TranslateService) {}

  info(msg: string, params?: Object): void {
    const msgObj = this.recordInfo(msg, params);
    PendingMessagesHandler.open(this.snackBar, msgObj);
  }

  recordInfo(msg: string, params?: Object): Message {
    const translatedMsg = this.translate.instant(msg, params);
    return PendingMessagesHandler.add(translatedMsg, 'INFO', '');
  }

  error(errorMsg: string, panelClass: Panel = ''): void {
    const msgObj = this.recordError(errorMsg, panelClass);
    PendingMessagesHandler.open(this.snackBar, msgObj);
  }

  recordError(errorMsg: string, panelClass: Panel = ''): Message {
    console.error(errorMsg);
    const hasMsg = errorMsg !== undefined && errorMsg !== '';
    const errorDisplay = hasMsg ? errorMsg : 'Something went wrong, please contact the support.';
    const translatedError = this.translate.instant(errorDisplay);
    RollbarHandler.error(translatedError);
    return PendingMessagesHandler.add(translatedError, 'ERROR', panelClass);
  }

  httpError(operation: string, resp: HttpErrorResponse): void {
    this.error(this.formatHttpError(operation, resp));
  }

  formatHttpError(operation: string, resp: HttpErrorResponse): string {
    const text = (resp.error && resp.error.message) || resp.statusText;
    return `(${operation}): ${text}`;
  }

  displayErrorAndStopChain(operation: string): OperatorFunction<any, any> {
    return catchError((err: any) => {
      if (err instanceof HttpErrorResponse) {
        this.httpError(operation, err);
      } else {
        this.error(`(${operation}): ${JSON.stringify(err)}`);
      }
      return EMPTY;
    });
  }
}

namespace PendingMessagesHandler {
  const PENDING_MESSAGES: Message[] = [];
  let snackBarRef: MatSnackBarRef<TextOnlySnackBar>;

  export function add(text: string, type: Type, panelClass: Panel): Message {
    const msg = new Message(text, panelClass, type);
    LATEST_MESSAGES.value.unshift(msg);
    if (LATEST_MESSAGES.value.length > 20) {
      LATEST_MESSAGES.value.pop();
    }
    return msg;
  }

  export function open(snackBar: MatSnackBar, msg: Message): void {
    PENDING_MESSAGES.push(msg);
    openNextPendingMessage(snackBar);
  }

  function openNextPendingMessage(snackBar: MatSnackBar): void {
    if (!snackBarRef && PENDING_MESSAGES.length > 0) {
      const msg = PENDING_MESSAGES.shift();
      openMsg(snackBar, msg);
    }
  }

  function openMsg(snackBar: MatSnackBar, msg: Message): void {
    snackBarRef = snackBar.open(msg.text, '', {
      duration: 5000,
      verticalPosition: 'bottom',
      panelClass: msg.panelClass,
    });
    snackBarRef.afterDismissed().subscribe(() => {
      snackBarRef = undefined;
      openNextPendingMessage(snackBar);
    });
  }
}
